/**
 * Numbering follows the boxes on the ICS 205-CG form, as printed by
 * `services/coms/src/routes/Forms/ICS_CG/forms/ICS205CG.ts`.
 *
 * Boxes 1-3 are the read-only header; box 4 is the channel table, whose columns
 * are listed separately in `ICS205_PRINTED_COLUMNS`; box 5 is the only free
 * text on the card; box 6 is the signature.
 *
 * The Coast Guard sheet numbers Incident Location as box 2, where the NIMS 205
 * the docx descriptor is read off puts Date/Time Prepared. That is why the card
 * labels its boxes from here rather than from the descriptor.
 */
export const ICS205_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  operationalPeriod: '3. Operational Period (Date/Time):',
  channels: '4. Basic Radio Channel Use:',
  specialInstructions: '5. Special Instructions:',
  preparedBy: '6. Prepared by (Communications Unit Leader):',
} as const;

/**
 * What each printed column of box 4 holds.
 *
 * Worth keeping written down: Assignment prints the work group's name where the
 * row has one and falls back to the free-text assignment, and Type is a screen
 * filter rather than a printed column.
 */
export const ICS205_PRINTED_COLUMNS = [
  { label: 'Ch #', source: 'channel_number' },
  { label: 'Function', source: 'function' },
  { label: 'Channel Name', source: 'channel_name' },
  { label: 'Assignment', source: 'work_group_name, else assignment' },
  { label: 'RX Freq', source: 'rx_frequency' },
  { label: 'RX Tone/NAC', source: 'rx_tone_nac' },
  { label: 'TX Freq', source: 'tx_frequency' },
  { label: 'TX Tone/NAC', source: 'tx_tone_nac' },
  { label: 'Mode', source: 'mode' },
  { label: 'Remarks', source: 'remarks' },
] as const;

export const ICS205_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

/** Mirrors `resolveOperationalPeriodValues`, which the PDF export uses. */
export const ICS205_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const ICS205_OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

/**
 * Channels are incident-scoped rather than period-scoped, so the same rows
 * print on every period's 205, and deleting one takes it off all of them.
 */
export const ICS205_CHANNELS_HINT =
  'Channels belong to the incident, not to one operational period, so a channel added here prints on every period\u2019s form and deleting one removes it from all of them.';

export const ICS205_SPECIAL_INSTRUCTIONS_HINT =
  'Shared with the incident information record, so the same text prints wherever else the incident reports special communications instructions.';
